import React, { useEffect, useMemo, useState } from "react";
import { CircularProgress } from "@material-ui/core";

import { useTextWorkspace } from "./useTextWorkspace";
import {
	filterEntries,
	getUniqueGroups,
	getUniqueScreens,
	getUniqueTags,
	paginateEntries,
	sortEntries,
} from "./selectors";
import { LABELS } from "./labels";
import {
	PageHeader,
	PageStat,
	PageStatLabel,
	PageStatValue,
	PageStats,
	PageSubtitle,
	PageTitle,
	PageTitleGroup,
} from "./styles";
import type { SortState, TextRegistryScope } from "./types";

import WorkspaceShell from "./components/WorkspaceShell";
import Toolbar from "./components/Toolbar";
import RegistryTable from "./components/RegistryTable";
import { translationLanguages } from "../../../services/StationsTextConfigService";
import BulkActionBar from "./components/BulkActionBar";
import DetectionInbox from "./components/DetectionInbox";
import GlossaryPanel from "./components/GlossaryPanel";
import EditDrawer from "./components/EditDrawer";
import FindReplaceDialog from "./components/FindReplaceDialog";
import PublishConfirmDialog from "./components/PublishConfirmDialog";
import Feedback from "./components/Feedback";

const VALID_SCOPES: TextRegistryScope[] = ["admin", "homepage", "stations"];

function readScopeFromUrl(): TextRegistryScope {
	if (typeof window === "undefined") return "admin";
	const params = new URLSearchParams(window.location.search);
	const raw = params.get("scope");
	return VALID_SCOPES.includes(raw as TextRegistryScope) ? (raw as TextRegistryScope) : "admin";
}

function writeScopeToUrl(scope: TextRegistryScope) {
	if (typeof window === "undefined") return;
	const url = new URL(window.location.href);
	url.searchParams.set("scope", scope);
	window.history.replaceState({}, "", url.toString());
}

interface TextWorkspaceProps {
	initialScope?: TextRegistryScope;
}

export default function TextWorkspace({ initialScope: propScope }: TextWorkspaceProps = {}) {
	const initialScope = useMemo(() => propScope || readScopeFromUrl(), [propScope]);
	const ws = useTextWorkspace(initialScope);
	const [publishDialogOpen, setPublishDialogOpen] = useState(false);

	useEffect(() => {
		writeScopeToUrl(ws.scope);
	}, [ws.scope]);

	const draftItems = ws.payload?.draft.items || [];
	const palette = ws.payload?.palette || ws.payload?.draft.palette || [];

	const screens = useMemo(() => getUniqueScreens(draftItems), [draftItems]);
	const groups = useMemo(() => getUniqueGroups(draftItems, ws.filters.screen), [draftItems, ws.filters.screen]);
	const tags = useMemo(() => getUniqueTags(draftItems), [draftItems]);

	const filtered = useMemo(() => filterEntries(draftItems, ws.filters), [draftItems, ws.filters]);
	const sorted = useMemo(() => sortEntries(filtered, ws.sort), [filtered, ws.sort]);
	const visible = useMemo(() => paginateEntries(sorted, ws.pagination), [sorted, ws.pagination]);

	const activeEntry = ws.activeKey ? draftItems.find((e) => e.key === ws.activeKey) || null : null;

	const newDetectionCount = ws.detections.filter((d) => d.status === "new").length;

	function handleSort(column: SortState["column"]) {
		if (ws.sort.column === column) {
			ws.setSort({ column, dir: ws.sort.dir === "asc" ? "desc" : "asc" });
		} else {
			ws.setSort({ column, dir: "asc" });
		}
	}

	function handleAddTagToRow(key: string, tag: string) {
		const item = draftItems.find((e) => e.key === key);
		if (!item) return;
		const existing: string[] = (item as any).tags || [];
		if (existing.includes(tag)) return;
		ws.updateDraftItem(key, { tags: [...existing, tag].slice(0, 8) } as any);
	}

	const langs = translationLanguages(ws.payload?.draft);

	function handleTranslationChange(key: string, lang: string, value: string) {
		const item = ws.payload?.draft.items.find((i) => i.key === key);
		ws.updateDraftItem(key, { translations: { ...(item?.translations || {}), [lang]: value } });
	}

	function handleRemoveTagFromRow(key: string, tag: string) {
		const item = draftItems.find((e) => e.key === key);
		if (!item) return;
		const existing: string[] = (item as any).tags || [];
		ws.updateDraftItem(key, { tags: existing.filter((t) => t !== tag) } as any);
	}

	function handlePublishClick() {
		setPublishDialogOpen(true);
	}

	async function handleConfirmPublish() {
		setPublishDialogOpen(false);
		await ws.publish();
	}

	if (ws.loading && !ws.payload) {
		return (
			<WorkspaceShell>
				<div style={{ display: "flex", justifyContent: "center", padding: 80 }}>
					<CircularProgress />
				</div>
			</WorkspaceShell>
		);
	}

	return (
		<WorkspaceShell
			detectionOpen={ws.detectionPanelOpen}
			detectionPanel={
				<DetectionInbox
					detections={ws.detections}
					onAdd={ws.mapDetection}
					onIgnore={ws.ignoreDetection}
				/>
			}
			glossaryPanel={
				ws.glossaryPanelOpen && (
					<GlossaryPanel
						terms={ws.glossary}
						onUpsert={ws.upsertGlossary}
						onRemove={ws.removeGlossary}
					/>
				)
			}
			drawer={
				<EditDrawer
					langs={langs}
					open={!!ws.activeKey}
					entry={activeEntry as any}
					activeTab={ws.drawerTab}
					scope={ws.scope}
					palette={palette}
					glossary={ws.glossary}
					dirty={ws.draftDirty}
					saving={ws.saving}
					onClose={() => ws.setActiveKey(null)}
					onTabChange={ws.setDrawerTab}
					onEntryChange={(patch) => ws.activeKey && ws.updateDraftItem(ws.activeKey, patch)}
					onRevert={(versionId) => ws.activeKey && ws.revertKeyToVersion(ws.activeKey, versionId)}
					onAddTag={(tag) => ws.activeKey && handleAddTagToRow(ws.activeKey, tag)}
					onRemoveTag={(tag) => ws.activeKey && handleRemoveTagFromRow(ws.activeKey, tag)}
					onResetToDefault={() =>
						ws.activeKey &&
						activeEntry?.defaultText &&
						ws.updateDraftItem(ws.activeKey, { currentText: activeEntry.defaultText })
					}
					onSaveDraft={ws.saveDraft}
				/>
			}>
			<PageHeader>
				<PageTitleGroup>
					<PageTitle>{LABELS.pageTitle}</PageTitle>
					<PageSubtitle>
						{ws.scope === "admin"
							? LABELS.scopeAdmin
							: ws.scope === "homepage"
							? LABELS.scopeHomepage
							: LABELS.scopeStations}
						{" · "}
						{draftItems.length.toLocaleString()} keys
					</PageSubtitle>
				</PageTitleGroup>
				<PageStats>
					<PageStat>
						<PageStatLabel>Filtered</PageStatLabel>
						<PageStatValue>{filtered.length.toLocaleString()}</PageStatValue>
					</PageStat>
					<PageStat>
						<PageStatLabel>Detections new</PageStatLabel>
						<PageStatValue tone={newDetectionCount > 0 ? "warn" : undefined}>
							{newDetectionCount}
						</PageStatValue>
					</PageStat>
					<PageStat>
						<PageStatLabel>Draft dirty</PageStatLabel>
						<PageStatValue tone={ws.draftDirty ? "accent" : undefined}>
							{ws.draftDirty ? "Yes" : "No"}
						</PageStatValue>
					</PageStat>
				</PageStats>
			</PageHeader>

			<Toolbar
				langs={langs}
				scope={ws.scope}
				filters={ws.filters}
				screens={screens}
				groups={groups}
				tags={tags}
				draftDirty={ws.draftDirty}
				saving={ws.saving}
				publishing={ws.publishing}
				detectionPanelOpen={ws.detectionPanelOpen}
				glossaryPanelOpen={ws.glossaryPanelOpen}
				newDetectionCount={newDetectionCount}
				onScopeChange={ws.setScope}
				onFilterChange={ws.setFilters}
				onFindReplace={() => ws.setFindReplace({ open: true })}
				onRefresh={ws.refresh}
				onSaveDraft={ws.saveDraft}
				onPublish={handlePublishClick}
				onToggleDetectionPanel={() => ws.setDetectionPanelOpen(!ws.detectionPanelOpen)}
				onToggleGlossaryPanel={() => ws.setGlossaryPanelOpen(!ws.glossaryPanelOpen)}
			/>

			<RegistryTable
				entries={visible}
				totalCount={sorted.length}
				sort={ws.sort}
				pagination={ws.pagination}
				selection={ws.selection}
				activeKey={ws.activeKey}
				glossary={ws.glossary}
				onSort={handleSort}
				onSelectAll={ws.selectAll}
				onSelectNone={ws.selectNone}
				onToggleSelect={ws.toggleSelect}
				onRowClick={ws.setActiveKey}
				onInlineEdit={(key, value) => ws.updateDraftItem(key, { currentText: value })}
				langs={langs}
				onInlineEditTranslation={handleTranslationChange}
				onAddTag={handleAddTagToRow}
				onRemoveTag={handleRemoveTagFromRow}
				onFilterByTag={(tag) => ws.setFilters({ tag })}
				onPaginate={ws.setPagination}
			/>

			<BulkActionBar
				selectedCount={ws.selection.size}
				publishing={ws.publishing}
				onApplyStyle={ws.applyBulkStyle}
				onAddTag={(tag) => ws.applyBulkTag(tag, "add")}
				onRemoveTag={(tag) => ws.applyBulkTag(tag, "remove")}
				onPublishSelected={ws.publishSelectedOnly}
				onClear={ws.selectNone}
			/>

			<FindReplaceDialog
				open={ws.findReplace.open}
				state={ws.findReplace}
				entries={filtered}
				filters={{ screen: ws.filters.screen, group: ws.filters.group }}
				selectionSize={ws.selection.size}
				onStateChange={ws.setFindReplace}
				onApply={ws.runFindReplace}
				onJumpToMatches={(find) => {
					ws.setFilters({ search: find });
					ws.setPagination({ page: 0 });
					ws.setFindReplace({ open: false });
				}}
				onClose={() => ws.setFindReplace({ open: false })}
			/>

			<PublishConfirmDialog
				open={publishDialogOpen}
				scope={ws.scope}
				publishing={ws.publishing}
				onConfirm={handleConfirmPublish}
				onCancel={() => setPublishDialogOpen(false)}
			/>

			<Feedback
				feedback={ws.feedback}
				onClose={ws.clearFeedback}
			/>
		</WorkspaceShell>
	);
}
